import { match } from "ts-pattern";

import type { Nullable, ValueOf } from "~/types/common";
import type { TCourseOptionTag, TCourseTag, TPatient } from "~/types/patient";

import { Arch } from "./prescription";

export enum CourseVersion {
  V1 = 1,
  V2 = 2,
  V3 = 3,
  V4 = 4,
}

export function versionize<T extends string>(str: T, version: CourseVersion) {
  return version == CourseVersion.V1 ? str : (`${str}_V${version}` as const);
}

export enum CourseId {
  FULL = 1,
  SHORT = 2,
  SINGLE_ARCH = 3,
  SUPER_SHORT = 4,
  CBCT = 5,
  PLAN_3D = 6,
  CHILDREN = 7,
  RETAINERS = 8,
  PRO = 9,
  TEEN = 10,
  CHILDREN_SHORT = 11,
  ADDITIONAL_ALIGNERS = 12,
  LIMITLESS = 13,
}

const CourseIdToCourseTag: Record<CourseId, TCourseTag> = {
  [CourseId.FULL]: "C_FULL",
  [CourseId.SHORT]: "C_SHORT",
  [CourseId.SINGLE_ARCH]: "C_ONE_JAW",
  [CourseId.SUPER_SHORT]: "C_SUPER_SHORT",
  [CourseId.CBCT]: "C_UNLIMITED",
  [CourseId.PLAN_3D]: "C_3D_PLAN",
  [CourseId.CHILDREN]: "C_CHILDREN",
  [CourseId.RETAINERS]: "C_RETAINERS",
  [CourseId.PRO]: "C_3DSMILE_PRO",
  [CourseId.TEEN]: "C_TEEN",
  [CourseId.CHILDREN_SHORT]: "C_CHILDREN_SHORT",
  [CourseId.ADDITIONAL_ALIGNERS]: "C_ADDITIONAL_ALIGNERS",
  [CourseId.LIMITLESS]: "C_LIMITLESS",
} as const;

export function getCourseTag(courseId: CourseId): TCourseTag {
  return CourseIdToCourseTag[courseId];
}

export const CO_TAG_FULL_3_STEPS_CBCT = "FULL_3_STEPS_CBCT";
export const CO_TAG_TEEN_3_STEPS_CBCT = "TEEN_3_STEPS_CBCT";

export function getRetainersCourseOptionTag(
  arch: Nullable<ValueOf<typeof Arch>>,
  quantity: Nullable<1 | 2>,
): TCourseOptionTag | undefined {
  return match({ arch, quantity })
    .with({ arch: Arch.BOTH, quantity: 2 }, () => "RETAINERS" as const)
    .with({ arch: Arch.UPPER, quantity: 2 }, () => "RETAINERS_ONE_ARCH" as const)
    .with({ arch: Arch.LOWER, quantity: 2 }, () => "RETAINERS_ONE_ARCH" as const)
    .with({ arch: Arch.BOTH, quantity: 1 }, () => "RETAINERS_ONE" as const)
    .with({ arch: Arch.UPPER, quantity: 1 }, () => "RETAINERS_ONE_ARCH_ONE" as const)
    .with({ arch: Arch.LOWER, quantity: 1 }, () => "RETAINERS_ONE_ARCH_ONE" as const)
    .otherwise(() => undefined);
}

export function getRetainersQuantityFromCourseOptionTag(
  course_option_tag: TPatient["course"]["course_option_tag"],
): 1 | 2 | null {
  return match(course_option_tag)
    .with("RETAINERS", "RETAINERS_ONE_ARCH", () => 2 as const)
    .with("RETAINERS_ONE", "RETAINERS_ONE_ARCH_ONE", () => 1 as const)
    .otherwise(() => null);
}

export enum Status {
  UNFILLED_CASE = "S_UNFILLED_CASE",
  RETAKE_IMPRESSIONS = "S_RETAKE_IMPRESSIONS",
  WAITING_FOR_A_PAYMENT = "S_WAITING_FOR_A_PAYMENT",
  _3D_PLAN_APPROVAL = "S_3D_PLAN_APPROVAL",
  PRODUCTION_PRINTING = "S_PRODUCTION_PRINTING",
  PRODUCTION_MOLDING = "S_PRODUCTION_MOLDING",
  PRODUCTION_MARKING = "S_PRODUCTION_MARKING",
  PRODUCTION_ALLOCATING = "S_PRODUCTION_ALLOCATING",
  PRODUCTION_FABRICATING = "S_PRODUCTION_FABRICATING",
  PRODUCTION_QUALITY_CONTROL = "S_PRODUCTION_QUALITY_CONTROL",
  FINAL_STAGE_READY = "S_FINAL_STAGE_READY",
  TREATMENT_FINISHED = "S_TREATMENT_FINISHED",
  CANCEL_TREATMENT = "S_CANCEL_TREATMENT",
  RETAINERS_RETAKE_IMPRESSIONS = "S_RETAINERS_RETAKE_IMPRESSIONS",
  RETAINERS_WAITING_FOR_A_PAYMENT = "S_RETAINERS_WAITING_FOR_A_PAYMENT",
}

// NOTE: Rendering logic in forms calls `is<Some>Course` functions even when no course has been selected.
// That's why we union course type with null.
type PatientCourseId = Nullable<TPatient["course"]["course_id"]>;

export function isRetainersCourse(course_id: PatientCourseId): course_id is CourseId.RETAINERS {
  return course_id == CourseId.RETAINERS;
}

export function isAdditionalAlignersCourse(course_id: PatientCourseId) {
  return course_id == CourseId.ADDITIONAL_ALIGNERS;
}

export function isChildrenCourse(course_id: PatientCourseId) {
  return course_id == CourseId.CHILDREN || course_id == CourseId.CHILDREN_SHORT;
}

export function isChildrenShortCourse(course_id: PatientCourseId) {
  return course_id == CourseId.CHILDREN_SHORT;
}

export function isCBCTCourse(course_id: PatientCourseId) {
  return course_id == CourseId.CBCT;
}

export function is3DPROCourse(course_id: PatientCourseId) {
  return course_id == CourseId.PRO;
}

export function isTeenCourse(course_id: PatientCourseId) {
  return course_id == CourseId.TEEN;
}

/**
 * Returns `true` if course only includes one arch treatment.
 */
export function isOneArchIncludedInCourse(courseId: PatientCourseId): boolean {
  const oneArchCourses = [CourseId.SINGLE_ARCH, CourseId.SUPER_SHORT] as const;
  return oneArchCourses.includes(courseId as CourseId);
}
